import {validateEmail, validatePassword} from "./regex";
import i18n from "../config/i18n";
import {theme} from "./theme/theme";

export const getEmailHelperText = (email: string, error: boolean): string | null => {
    if (error) {
        if (!email.length) {
            return i18n.t('field_required')
        }
        if (!validateEmail(email)) {
            return i18n.t('email_field_not_valid')
        }
    }
    return null
}

export const getPasswordHelperText = (password: string, error: boolean): string | null => {
    if (error) {
        if (!password.length) {
            return i18n.t('field_required')
        }
        if (!validatePassword(password)) {
            return i18n.t('password_regex_not_valid')
        }
    }
    return null
}

export const getRequiredFieldHelperText = (text: string, error: boolean): string | null => {
    if (error) {
        if (!text.length) {
            return i18n.t('field_required')
        }
    }
    return null
}

export const getRandomNumber = (min: number, max: number) => {
    return Math.floor(Math.random() * (max - min)) + min
}

export const formatDate = (dateString: string | null, withTime: boolean = true) => {
    if (!dateString) {
        return '-'
    }
    const d = new Date(dateString)
    let result = ('0' + d.getDate()).slice(-2) + "." + ('0' + (d.getMonth() + 1)).slice(-2) + "." + d.getFullYear()
    if (withTime) {
        result += " " +
            ('0' + d.getHours()).slice(-2) + ":" + ('0' + d.getMinutes()).slice(-2) + ":" + ('0' + d.getSeconds()).slice(-2);
    }
    return result
}

export const arrayIntoArrays = (array: any[], numElements: number) => {
    const resultArray: any[] = []
    let arrayOfElements: any[] = []
    array.forEach((el, idx) => {
        if (idx % numElements === 0 && idx !== 0) {
            resultArray.push(arrayOfElements)
            arrayOfElements = []
        }
        arrayOfElements.push(el)
    })
    if (arrayOfElements.length) {
        resultArray.push(arrayOfElements)
    }

    return resultArray
}

export const subtractYearsFromDate = (date: Date, years: number) => {
    const result = new Date(date);
    result.setFullYear(result.getFullYear() - years);
    return result;
}

export const subtractMonthsFromDate = (date: Date, months: number) => {
    const result = new Date(date);
    result.setMonth(result.getMonth() - months);
    return result;
}

export const addDaysToDate = (date: Date, days: number) => {
    const result = new Date(date);
    result.setDate(result.getDate() + days);
    return result;
};

export interface IRgbaColors {
    withAlpha: string
    noAlpha: string
}

export const generateRandomRgbaColors = (): IRgbaColors => {
    const red = getRandomNumber(52, 235)
    const green = getRandomNumber(52, 235)
    const blue = getRandomNumber(52, 235)

    return {withAlpha: `rgba(${red}, ${green}, ${blue}, 0.4)`, noAlpha: `rgba(${red}, ${green}, ${blue}, 1)`};
}

export const getLinearProgressColor = (ticketsCount: number, maxTickets: number) => {
    if (ticketsCount >= maxTickets) {
        return "error"
    }

    return "success"
}

export const formatDateWithSlash = (d: Date) => {
    return ("0" + d.getDate()).slice(-2) + "/" + ("0" + (d.getMonth() + 1)).slice(-2) + "/" +
        d.getFullYear()
}

export const getSafeBackendErrorMessage = (message: string | string[] | undefined, backupMessage?: string) => {
    if (message && !Array.isArray(message)) return message
    if (backupMessage) return backupMessage
    return i18n.t('error_occurred')
}

export const validatePhoneNumber = (name: string, value: string): boolean => {
    return name.includes("phone") && !/^\+?\d*$/.test(value)
}

export const getTextColorOnRecommended = (recommended: boolean) => recommended ? theme.palette.tertiary.main : theme.palette.primary.main

export const toISOLocal = (d: Date | null) => {
    if (!d) {
        return d
    }
    const pad = (i: number) => (i < 10) ? '0' + i : i

    const Y = d.getFullYear();
    const m = d.getMonth() + 1;
    const D = d.getDate();
    const H = d.getHours();
    const M = d.getMinutes();
    const S = d.getSeconds();
    return Y + '-' + pad(m) + '-' + pad(D) + 'T' + pad(H) + ':' + pad(M) + ':' + pad(S);
}

export const toHex = (str: string) => {
    return Array.from(new TextEncoder().encode(str), byte =>
        byte.toString(16).padStart(2, '0')  // Convert each byte to hexadecimal
    ).join('');
}

export const isValidUrl = (text: string): boolean => {
    try {
        new URL(text);
        return true;
    } catch (e) {
        return false;
    }
}

export const getLastPathSegment = (urlString: string): string => {
    // 1. Parse the string as a URL object
    const url = new URL(urlString);

    // 2. Get the pathname, e.g. "/docs/folder/file.pdf"
    let pathName = url.pathname;

    // 3. If the path ends with a slash (e.g. "/folder/"), remove it
    //    so we don't get an empty string as the last segment
    if (pathName.endsWith("/")) {
        pathName = pathName.slice(0, -1);
    }

    // 4. The last segment is now after the final "/"
    //    e.g. "file.pdf" or "folder"
    return pathName.substring(pathName.lastIndexOf("/") + 1);
}

export const urlBase64ToUint8Array = (base64String: string) => {
    const padding = '='.repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding)
        .replace(/-/g, '+')
        .replace(/_/g, '/');

    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);

    for (let i = 0; i < rawData.length; ++i) {
        outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
}

export const formatPrice = (price: number) => {
    const formatter = new Intl.NumberFormat('mk-MK', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
    });

    return `${formatter.format(price)} MKD`;
}

export const formatNumber = (price: number) => {
    const formatter = new Intl.NumberFormat('mk-MK', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
    });

    return formatter.format(price);
}

/**
 * Generate an array of YYYY-MM strings from the creation month to last month.
 * @param createdAt ISO date string (e.g., "2025-05-22T17:08:15.053205")
 * @returns string[] like ["2025-05", "2025-06", ...]
 */
export const generateMonthOptions = (createdAt: string): string[] => {
    const start = new Date(createdAt);
    const now = new Date();

    // Set start date to first of its month
    start.setDate(1);

    // Set end date to first day of current month (exclude current month)
    const end = new Date(now.getFullYear(), now.getMonth(), 1);

    const result: string[] = [];

    // Loop through months until we reach end
    while (start < end) {
        const year = start.getFullYear();
        const month = String(start.getMonth() + 1).padStart(2, "0"); // months are 0-based
        result.push(`${year}-${month}`);

        // Move to next month
        start.setMonth(start.getMonth() + 1);
    }

    return result;
}
